// ══════════════════════════════════════════════════════════════
// District88 — Supabase Data Service Layer
// All CRUD operations for profiles, posts, listings, etc.
// ══════════════════════════════════════════════════════════════

import { supabase } from '../lib/supabase';
import { compressImages } from '../lib/imageCompression';
import type { Profile, Post, Comment, Listing, Order, Activity, Follow, Drop } from '../types/database';

// ── Profiles ─────────────────────────────────────────────────

export const profileService = {
  async getById(id: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();
    return { data: data as Profile | null, error };
  },

  async getByUsername(username: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username)
      .single();
    return { data: data as Profile | null, error };
  },

  async getAll(limit = 20) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as Profile[], error };
  },

  async search(query: string, limit = 20) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
      .limit(limit);
    return { data: (data || []) as Profile[], error };
  },

  async update(id: string, updates: Partial<Profile>) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data: data as Profile | null, error };
  },

  async uploadAvatar(userId: string, file: File) {
    const ext = file.name.split('.').pop();
    const path = `avatars/${userId}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true });

    if (uploadError) return { url: null, error: uploadError };

    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);

    await supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', userId);

    return { url: publicUrl, error: null };
  },
};


// ── Posts ─────────────────────────────────────────────────────

export const postService = {
  async getFeed(options: { limit?: number; offset?: number; type?: string } = {}) {
    const { limit = 20, offset = 0, type } = options;

    let query = supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (type) query = query.eq('type', type);

    const { data, error } = await query;
    return { data: (data || []) as (Post & { author: Profile })[], error };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .eq('id', id)
      .single();
    return { data: data as (Post & { author: Profile }) | null, error };
  },

  async getByAuthor(authorId: string, limit = 20) {
    const { data, error } = await supabase
      .from('posts')
      .select('*, author:profiles!author_id(*)')
      .eq('author_id', authorId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as (Post & { author: Profile })[], error };
  },

  async create(post: { author_id: string; content: string; type: string; images?: string[]; tags?: string[]; poll_options?: Record<string, number> | null; community_id?: string | null }) {
    const { data, error } = await supabase
      .from('posts')
      .insert({
        author_id: post.author_id,
        content: post.content,
        type: post.type,
        images: post.images || [],
        tags: post.tags || [],
        poll_options: post.poll_options || null,
        community_id: post.community_id || null,
      })
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data: data as (Post & { author: Profile }) | null, error };
  },

  async update(id: string, updates: Partial<Post>) {
    const { data, error } = await supabase
      .from('posts')
      .update(updates)
      .eq('id', id)
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data: data as (Post & { author: Profile }) | null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('posts').delete().eq('id', id);
    return { error };
  },

  async uploadImages(files: File[]) {
    const compressed = await compressImages(files);
    const urls: string[] = [];
    for (const file of compressed) {
      const ext = file.name.split('.').pop();
      const path = `posts/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('posts').upload(path, file);
      if (!error) {
        const { data: { publicUrl } } = supabase.storage.from('posts').getPublicUrl(path);
        urls.push(publicUrl);
      }
    }
    return urls;
  },
};


// ── Comments ─────────────────────────────────────────────────

export const commentService = {
  async getByPost(postId: string) {
    const { data, error } = await supabase
      .from('comments')
      .select('*, author:profiles!author_id(*)')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    return { data: (data || []) as (Comment & { author: Profile })[], error };
  },

  async create(comment: { post_id: string; author_id: string; content: string; parent_id?: string | null }) {
    const { data, error } = await supabase
      .from('comments')
      .insert({
        post_id: comment.post_id,
        author_id: comment.author_id,
        content: comment.content,
        parent_id: comment.parent_id || null,
      })
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data: data as (Comment & { author: Profile }) | null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('comments').delete().eq('id', id);
    return { error };
  },
};


// ── Likes / Reactions ────────────────────────────────────────

export const likeService = {
  async toggle(postId: string, userId: string, reactionType = 'like') {
    // Check if already liked
    const { data: existing } = await supabase
      .from('likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();

    if (existing) {
      // Unlike
      await supabase.from('likes').delete().eq('id', existing.id);
      return { liked: false };
    } else {
      // Like
      await supabase.from('likes').insert({
        post_id: postId,
        user_id: userId,
        reaction_type: reactionType,
      });
      return { liked: true };
    }
  },

  async hasLiked(postId: string, userId: string) {
    const { data } = await supabase
      .from('likes')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .maybeSingle();
    return !!data;
  },

  async getLikedPostIds(userId: string, postIds: string[]) {
    if (postIds.length === 0) return new Set<string>();
    const { data } = await supabase
      .from('likes')
      .select('post_id')
      .eq('user_id', userId)
      .in('post_id', postIds);
    return new Set((data || []).map(l => l.post_id));
  },
};

// ── Saves (bookmarks) ───────────────────────────────────────

export const saveService = {
  async toggle(postId: string, userId: string) {
    try {
      const { data: existing } = await supabase
        .from('saved_posts')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        await supabase.from('saved_posts').delete().eq('id', existing.id);
        return { saved: false };
      } else {
        await supabase.from('saved_posts').insert({ post_id: postId, user_id: userId });
        return { saved: true };
      }
    } catch { return { saved: false }; }
  },

  async getSavedPostIds(userId: string, postIds: string[]) {
    if (postIds.length === 0) return new Set<string>();
    try {
      const { data } = await supabase
        .from('saved_posts')
        .select('post_id')
        .eq('user_id', userId)
        .in('post_id', postIds);
      return new Set((data || []).map(s => s.post_id));
    } catch { return new Set<string>(); }
  },

  async getByUser(userId: string, limit = 50) {
    try {
      const { data } = await supabase
        .from('saved_posts')
        .select('post_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      return data || [];
    } catch { return []; }
  },
};

// ── Reposts ─────────────────────────────────────────────────

export const repostService = {
  async toggle(postId: string, userId: string) {
    try {
      const { data: existing } = await supabase
        .from('reposts')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        await supabase.from('reposts').delete().eq('id', existing.id);
        return { reposted: false };
      } else {
        await supabase.from('reposts').insert({ post_id: postId, user_id: userId });
        return { reposted: true };
      }
    } catch { return { reposted: false }; }
  },

  async getRepostedPostIds(userId: string, postIds: string[]) {
    if (postIds.length === 0) return new Set<string>();
    try {
      const { data } = await supabase
        .from('reposts')
        .select('post_id')
        .eq('user_id', userId)
        .in('post_id', postIds);
      return new Set((data || []).map(r => r.post_id));
    } catch { return new Set<string>(); }
  },

  async getByUser(userId: string, limit = 50) {
    try {
      const { data } = await supabase
        .from('reposts')
        .select('post_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      return data || [];
    } catch { return []; }
  },

  async getByUserWithPosts(userId: string, limit = 50) {
    try {
      const { data: repostRows } = await supabase
        .from('reposts')
        .select('post_id, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (!repostRows || repostRows.length === 0) return [];
      const postIds = repostRows.map(r => r.post_id);
      const { data: posts } = await supabase
        .from('posts')
        .select('*, author:profiles!author_id(*)')
        .in('id', postIds);
      return (posts || []) as any[];
    } catch { return []; }
  },
};


// ── Listings (Market) ────────────────────────────────────────

export const listingService = {
  async getAll(options: { limit?: number; offset?: number; status?: string; brand?: string; category?: string; search?: string; sortBy?: string; districtStore?: boolean } = {}) {
    const { limit = 20, offset = 0, status = 'active', brand, category, search, sortBy = 'created_at', districtStore } = options;

    let query = supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('status', status)
      .order(sortBy, { ascending: false })
      .range(offset, offset + limit - 1);

    if (districtStore === true) {
      query = query.eq('is_district_store', true);
    } else if (districtStore === false) {
      query = query.eq('is_district_store', false);
    }

    if (brand) query = query.eq('brand', brand);
    if (category) query = query.eq('category', category);
    if (search) query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%,brand.ilike.%${search}%`);

    const { data, error } = await query;
    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('id', id)
      .single();

    // Increment view count
    if (data) {
      try {
        await supabase.rpc('increment_views', { listing_id: id });
      } catch {
        // Fallback: direct update (works without RPC function)
        await supabase.from('listings').update({ views: (data.views || 0) + 1 }).eq('id', id);
      }
    }

    return { data: data as (Listing & { seller: Profile }) | null, error };
  },

  async getByIds(ids: string[]) {
    if (ids.length === 0) return { data: [] as (Listing & { seller: Profile })[], error: null };
    const { data, error } = await supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .in('id', ids);
    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  async getBySeller(sellerId: string) {
    const { data, error } = await supabase
      .from('listings')
      .select('*, seller:profiles!seller_id(*)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false });
    return { data: (data || []) as (Listing & { seller: Profile })[], error };
  },

  async create(listing: {
    seller_id: string;
    title: string;
    description?: string;
    price: number;
    original_price?: number;
    condition: string;
    size?: string;
    brand?: string;
    category?: string;
    images?: string[];
    is_auction?: boolean;
    auction_ends_at?: string;
    status?: string;
    available_sizes?: string[];
    quantity?: number;
  }) {
    const { data, error } = await supabase
      .from('listings')
      .insert({
        seller_id: listing.seller_id,
        title: listing.title,
        description: listing.description || '',
        price: listing.price,
        original_price: listing.original_price || null,
        condition: listing.condition || 'DS',
        size: listing.size || null,
        brand: listing.brand || null,
        category: listing.category || null,
        images: listing.images || [],
        is_auction: listing.is_auction || false,
        auction_ends_at: listing.auction_ends_at || null,
        status: listing.status || 'active',
        available_sizes: listing.available_sizes || [],
        quantity: listing.quantity || 1,
      })
      .select('*')
      .single();
    return { data: data as Listing | null, error };
  },

  async update(id: string, updates: Partial<Listing>) {
    const { data, error } = await supabase
      .from('listings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data: data as Listing | null, error };
  },

  async delete(id: string) {
    const { error } = await supabase.from('listings').delete().eq('id', id);
    return { error };
  },

  async uploadImages(files: File[]): Promise<{ urls: string[]; errors: string[] }> {
    const compressed = await compressImages(files);
    const urls: string[] = [];
    const errors: string[] = [];
    for (const file of compressed) {
      const ext = file.name.split('.').pop();
      const path = `listings/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('listings').upload(path, file);
      if (error) {
        console.error(`Image upload failed for ${file.name}:`, error.message);
        errors.push(`${file.name}: ${error.message}`);
      } else {
        const { data: { publicUrl } } = supabase.storage.from('listings').getPublicUrl(path);
        urls.push(publicUrl);
      }
    }
    return { urls, errors };
  },
};


// ── Orders ───────────────────────────────────────────────────

export const orderService = {
  async getById(orderId: string) {
    const { data, error } = await supabase
      .from('orders')
      .select('*, listing:listings(*), buyer:profiles!buyer_id(*), seller:profiles!seller_id(*)')
      .eq('id', orderId)
      .maybeSingle();
    return { data: data as Order | null, error };
  },

  async getByBuyer(buyerId: string) {
    const { data, error } = await supabase
      .from('orders')
      .select('*, listing:listings(*), seller:profiles!seller_id(*)')
      .eq('buyer_id', buyerId)
      .order('created_at', { ascending: false });
    return { data: (data || []) as Order[], error };
  },

  async getBySeller(sellerId: string) {
    const { data, error } = await supabase
      .from('orders')
      .select('*, listing:listings(*), buyer:profiles!buyer_id(*)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false });
    return { data: (data || []) as Order[], error };
  },

  async create(order: { listing_id: string; buyer_id: string; seller_id: string; price: number; shipping_cost?: number }) {
    const { data, error } = await supabase
      .from('orders')
      .insert({
        listing_id: order.listing_id,
        buyer_id: order.buyer_id,
        seller_id: order.seller_id,
        price: order.price,
        shipping_cost: order.shipping_cost || null,
      })
      .select()
      .single();

    // Mark listing as sold
    if (data) {
      await supabase.from('listings').update({ status: 'sold' }).eq('id', order.listing_id);
    }

    return { data: data as Order | null, error };
  },

  async updateStatus(id: string, status: string, trackingNumber?: string) {
    const updates: Record<string, any> = { status };
    if (trackingNumber) updates.tracking_number = trackingNumber;
    const { data, error } = await supabase
      .from('orders')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data: data as Order | null, error };
  },
};


// ── Follows ──────────────────────────────────────────────────

export const followService = {
  async toggle(followerId: string, followingId: string) {
    const { data: existing } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .maybeSingle();

    if (existing) {
      await supabase.from('follows').delete().eq('id', existing.id);
      return { following: false };
    } else {
      await supabase.from('follows').insert({
        follower_id: followerId,
        following_id: followingId,
      });
      return { following: true };
    }
  },

  async isFollowing(followerId: string, followingId: string) {
    const { data } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .maybeSingle();
    return !!data;
  },

  async getFollowers(userId: string) {
    const { data, error } = await supabase
      .from('follows')
      .select('follower:profiles!follower_id(*)')
      .eq('following_id', userId);
    return { data: (data || []).map(d => (d as any).follower as Profile), error };
  },

  async getFollowing(userId: string) {
    const { data, error } = await supabase
      .from('follows')
      .select('following:profiles!following_id(*)')
      .eq('follower_id', userId);
    return { data: (data || []).map(d => (d as any).following as Profile), error };
  },
};


// ── Activities (real-time ticker) ────────────────────────────

export const activityService = {
  async getRecent(limit = 50) {
    const { data, error } = await supabase
      .from('activities')
      .select('*, user:profiles!user_id(*)')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as (Activity & { user: Profile })[], error };
  },

  async create(activity: { type: string; user_id: string; message: string; metadata?: Record<string, any> }) {
    const { data, error } = await supabase
      .from('activities')
      .insert({
        type: activity.type as any,
        user_id: activity.user_id,
        message: activity.message,
        metadata: activity.metadata || null,
      })
      .select()
      .single();
    return { data: data as Activity | null, error };
  },

  // Real-time subscription
  subscribeToNew(callback: (activity: Activity) => void) {
    const channel = supabase
      .channel('activities-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'activities' },
        (payload) => callback(payload.new as Activity)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};


// ── Notifications ────────────────────────────────────────────

export const notificationService = {
  async getForUser(userId: string, limit = 30) {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async getUnreadCount(userId: string) {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);
    return { count: count || 0, error };
  },

  async markAsRead(notificationId: string) {
    return supabase.from('notifications').update({ read: true }).eq('id', notificationId);
  },

  async markAllAsRead(userId: string) {
    return supabase.from('notifications').update({ read: true }).eq('user_id', userId).eq('read', false);
  },

  async create(notification: { user_id: string; type: string; title: string; message: string; link?: string; actor_id?: string }) {
    return supabase.from('notifications').insert(notification).select().single();
  },

  subscribeToNew(userId: string, callback: (notification: any) => void) {
    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        (payload) => callback(payload.new)
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  },
};


// ── Messages ─────────────────────────────────────────────────

export const messageService = {
  async getConversations(userId: string) {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .contains('participant_ids', JSON.stringify([userId]))
      .order('updated_at', { ascending: false });
    return { data: (data || []), error };
  },

  async getMessages(conversationId: string) {
    const { data, error } = await supabase
      .from('messages')
      .select('*, sender:profiles!sender_id(*)')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    return { data: (data || []), error };
  },

  async sendMessage(conversationId: string, senderId: string, content: string) {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        content,
      })
      .select('*, sender:profiles!sender_id(*)')
      .single();

    // Update conversation last_message
    if (data) {
      await supabase.from('conversations').update({
        last_message: content,
        updated_at: new Date().toISOString(),
      }).eq('id', conversationId);
    }

    return { data, error };
  },

  async createConversation(participantIds: string[]) {
    const { data, error } = await supabase
      .from('conversations')
      .insert({ participant_ids: participantIds })
      .select()
      .single();
    return { data, error };
  },

  // Real-time subscription
  subscribeToMessages(conversationId: string, callback: (message: any) => void) {
    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        (payload) => callback(payload.new)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};

// ── Stories (24hr) ──────────────────────────────────────────

export const storyService = {
  async getActive() {
    const { data, error } = await supabase
      .from('stories')
      .select('*, author:profiles!author_id(*)')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('stories')
      .select('*, author:profiles!author_id(*)')
      .eq('author_id', userId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async create(story: { author_id: string; content?: string; image_url?: string; type?: string; background_color?: string }) {
    const { data, error } = await supabase
      .from('stories')
      .insert(story)
      .select('*, author:profiles!author_id(*)')
      .single();
    return { data, error };
  },

  async markViewed(storyId: string, viewerId: string) {
    await supabase.from('story_views').upsert({ story_id: storyId, viewer_id: viewerId }, { onConflict: 'story_id,viewer_id' });
  },

  async getViewedIds(viewerId: string, storyIds: string[]): Promise<Set<string>> {
    if (storyIds.length === 0) return new Set();
    const { data } = await supabase
      .from('story_views')
      .select('story_id')
      .eq('viewer_id', viewerId)
      .in('story_id', storyIds);
    return new Set((data || []).map(v => v.story_id));
  },

  async delete(storyId: string) {
    return supabase.from('stories').delete().eq('id', storyId);
  },

  async uploadImage(file: File): Promise<string> {
    const ext = file.name.split('.').pop();
    const path = `stories/${Date.now()}-${Math.random().toString(36).substring(2)}.${ext}`;
    const { error } = await supabase.storage.from('posts').upload(path, file);
    if (error) return '';
    const { data: urlData } = supabase.storage.from('posts').getPublicUrl(path);
    return urlData.publicUrl;
  },
};

// ── Endorsements ────────────────────────────────────────────

export const endorsementService = {
  async getForUser(userId: string) {
    const { data, error } = await supabase
      .from('endorsements')
      .select('*, endorser:profiles!endorser_id(*)')
      .eq('endorsed_id', userId)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async create(endorserId: string, endorsedId: string, category: string, message: string) {
    const { data, error } = await supabase
      .from('endorsements')
      .insert({ endorser_id: endorserId, endorsed_id: endorsedId, category, message })
      .select()
      .single();
    return { data, error };
  },

  async delete(endorserId: string, endorsedId: string, category: string) {
    return supabase.from('endorsements').delete().match({ endorser_id: endorserId, endorsed_id: endorsedId, category });
  },
};

// ── Closet / Vault ──────────────────────────────────────

export interface ClosetItemRow {
  id: string;
  user_id: string;
  name: string;
  brand: string | null;
  category: string;
  image_url: string | null;
  purchase_price: number | null;
  current_value: number | null;
  condition: string | null;
  is_grail: boolean;
  acquired_at: string | null;
  wear_count: number | null;
  last_worn: string | null;
  notes: string | null;
  created_at: string;
  story?: string;
  memory_tag?: string;
  never_sell?: boolean;
  is_public?: boolean;
  photos?: string[];
  outfits?: string[];
  condition_history?: { date: string; condition: string; note: string }[];
  legacy_note?: string;
}

export const closetService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('closet_items')
      .select('*')
      .eq('user_id', userId)
      .order('acquired_at', { ascending: false, nullsFirst: false });
    return { data: (data || []) as ClosetItemRow[], error };
  },

  async getByUserPublic(userId: string) {
    const { data, error } = await supabase
      .from('closet_items')
      .select('*')
      .eq('user_id', userId)
      .eq('is_public', true)
      .order('acquired_at', { ascending: false, nullsFirst: false });
    return { data: (data || []) as ClosetItemRow[], error };
  },

  async create(item: {
    user_id: string;
    name: string;
    brand?: string;
    category?: string;
    image_url?: string;
    purchase_price?: number;
    current_value?: number;
    condition?: string;
    is_grail?: boolean;
    story?: string;
    memory_tag?: string;
    never_sell?: boolean;
    is_public?: boolean;
    photos?: string[];
    outfits?: string[];
    condition_history?: { date: string; condition: string; note: string }[];
    legacy_note?: string;
  }) {
    const row: Record<string, any> = {
      user_id: item.user_id,
      name: item.name,
      brand: item.brand || null,
      category: item.category || 'sneakers',
      image_url: item.image_url || null,
      purchase_price: item.purchase_price ?? null,
      current_value: item.current_value ?? item.purchase_price ?? null,
      condition: item.condition || 'Good',
      is_grail: item.is_grail ?? false,
      notes: item.story || '',
      story: item.story || '',
      memory_tag: item.memory_tag || 'none',
      never_sell: item.never_sell ?? false,
      is_public: item.is_public ?? true,
      photos: item.photos || [],
      outfits: item.outfits || [],
      condition_history: item.condition_history || [],
      legacy_note: item.legacy_note || '',
      acquired_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('closet_items').insert(row).select().single();
    return { data: data as ClosetItemRow | null, error };
  },

  async update(itemId: string, updates: Record<string, any>) {
    const { data, error } = await supabase.from('closet_items').update(updates).eq('id', itemId).select().single();
    return { data: data as ClosetItemRow | null, error };
  },

  async delete(itemId: string) {
    return supabase.from('closet_items').delete().eq('id', itemId);
  },

  async markWorn(itemId: string) {
    const { data: item } = await supabase.from('closet_items').select('wear_count').eq('id', itemId).single();
    const count = (item as any)?.wear_count ?? 0;
    const { data, error } = await supabase
      .from('closet_items')
      .update({ last_worn: new Date().toISOString(), wear_count: count + 1 })
      .eq('id', itemId)
      .select()
      .single();
    return { data, error };
  },
};

// ── Wishlists / Grail Tracker ──────────────────────────────

export interface WishlistRow {
  id: string;
  user_id: string;
  name: string;
  brand: string | null;
  image_url: string | null;
  max_price: number | null;
  target_price: number | null;
  saved_amount: number | null;
  priority: string | null;
  notes: string | null;
  found: boolean;
  created_at: string;
}

export const wishlistService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('wishlists')
      .select('*')
      .eq('user_id', userId)
      .eq('found', false)
      .order('created_at', { ascending: false });
    return { data: (data || []) as WishlistRow[], error };
  },

  async create(item: {
    user_id: string;
    name: string;
    brand?: string;
    image_url?: string;
    max_price?: number;
    target_price?: number;
    saved_amount?: number;
    priority?: string;
    notes?: string;
  }) {
    const row = {
      ...item,
      target_price: item.target_price ?? item.max_price ?? null,
      saved_amount: item.saved_amount ?? 0,
      found: false,
    };
    const { data, error } = await supabase.from('wishlists').insert(row).select().single();
    return { data: data as WishlistRow | null, error };
  },

  async update(itemId: string, updates: Record<string, any>) {
    const { data, error } = await supabase.from('wishlists').update(updates).eq('id', itemId).select().single();
    return { data: data as WishlistRow | null, error };
  },

  async delete(itemId: string) {
    return supabase.from('wishlists').delete().eq('id', itemId);
  },
};

// ── Achievements ───────────────────────────────────────────

export const achievementService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
    return { data: data || [], error };
  },
};

// ── User Settings ──────────────────────────────────────────

export const userSettingsService = {
  async get(userId: string) {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data, error };
  },

  async upsert(userId: string, settings: Record<string, any>) {
    const { data, error } = await supabase
      .from('user_settings')
      .upsert({ user_id: userId, ...settings, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      .select()
      .single();
    return { data, error };
  },
};

// ── Profile Views ──────────────────────────────────────────

export const profileViewService = {
  async log(profileId: string, viewerId?: string) {
    await supabase.from('profile_views').insert({ profile_id: profileId, viewer_id: viewerId || null });
  },

  async getCount(profileId: string) {
    const { count } = await supabase.from('profile_views').select('*', { count: 'exact', head: true }).eq('profile_id', profileId);
    return count || 0;
  },

  async getRecent(profileId: string, limit = 10) {
    const { data } = await supabase
      .from('profile_views')
      .select('*, viewer:profiles!viewer_id(*)')
      .eq('profile_id', profileId)
      .not('viewer_id', 'is', null)
      .order('viewed_at', { ascending: false })
      .limit(limit);
    return data || [];
  },
};

// ── Profile Sections ───────────────────────────────────────

export const profileSectionService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('profile_sections')
      .select('*')
      .eq('user_id', userId)
      .order('sort_order', { ascending: true });
    return { data: data || [], error };
  },

  async upsert(section: { id?: string; user_id: string; section_type: string; title: string; content: any; sort_order?: number; is_visible?: boolean }) {
    const { data, error } = await supabase
      .from('profile_sections')
      .upsert(section)
      .select()
      .single();
    return { data, error };
  },

  async delete(sectionId: string) {
    return supabase.from('profile_sections').delete().eq('id', sectionId);
  },

  async reorder(userId: string, orderedIds: string[]) {
    const updates = orderedIds.map((id, i) => supabase.from('profile_sections').update({ sort_order: i }).eq('id', id));
    await Promise.all(updates);
  },
};

// ── User Interests ─────────────────────────────────────────

// ── Drops ─────────────────────────────────────────────────

export const dropService = {
  async getAll(opts: { status?: string; limit?: number; offset?: number } = {}) {
    let q = supabase.from('drops').select('*').order('created_at', { ascending: false });
    if (opts.status) q = q.eq('status', opts.status);
    if (opts.limit) q = q.limit(opts.limit);
    if (opts.offset) q = q.range(opts.offset, opts.offset + (opts.limit || 20) - 1);
    const { data, error } = await q;
    return { data: (data || []) as Drop[], error };
  },

  async getPublished(limit = 50) {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .in('status', ['published', 'featured'])
      .eq('visibility', 'public')
      .order('release_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: (data || []) as Drop[], error };
  },

  async getForRadar(limit = 100) {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .in('status', ['published', 'featured'])
      .eq('visibility', 'public')
      .order('release_date', { ascending: true, nullsFirst: true })
      .limit(limit);
    return { data: (data || []) as Drop[], error };
  },

  async getFeatured() {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .eq('status', 'featured')
      .eq('visibility', 'public')
      .order('created_at', { ascending: false });
    return { data: (data || []) as Drop[], error };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('drops')
      .select('*')
      .eq('id', id)
      .single();
    return { data: data as Drop | null, error };
  },

  async create(drop: Partial<Drop>) {
    const { data, error } = await supabase.from('drops').insert(drop).select().single();
    return { data: data as Drop | null, error };
  },

  async update(id: string, updates: Partial<Drop>) {
    const { data, error } = await supabase.from('drops').update(updates).eq('id', id).select().single();
    return { data: data as Drop | null, error };
  },

  async delete(id: string) {
    return supabase.from('drops').delete().eq('id', id);
  },

  async incrementViews(id: string) {
    try {
      await supabase.rpc('increment_drop_views', { drop_id: id });
    } catch {
      // RPC may not exist; silently skip
    }
  },

  async scrapeUrl(url: string) {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/scrape-product`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ url }),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Scrape failed' }));
      return { data: null, error: err.error || 'Failed to fetch product data' };
    }
    const data = await res.json();
    return { data, error: null };
  },
};

// ── Drop Watchlist ────────────────────────────────────────

export const dropWatchlistService = {
  async isWatching(userId: string, dropId: string) {
    const { data } = await supabase
      .from('drop_watchlist')
      .select('id, notify')
      .eq('user_id', userId)
      .eq('drop_id', dropId)
      .maybeSingle();
    return { watching: !!data, notify: data?.notify ?? false, id: data?.id };
  },

  async add(userId: string, dropId: string, notify = true) {
    const { error } = await supabase.from('drop_watchlist').insert({ user_id: userId, drop_id: dropId, notify });
    if (!error) {
      try { await supabase.rpc('increment_drop_watchlist', { did: dropId }); } catch {}
    }
    return { error };
  },

  async remove(userId: string, dropId: string) {
    const { error } = await supabase.from('drop_watchlist').delete().eq('user_id', userId).eq('drop_id', dropId);
    return { error };
  },

  async toggleNotify(userId: string, dropId: string, notify: boolean) {
    return supabase.from('drop_watchlist').update({ notify }).eq('user_id', userId).eq('drop_id', dropId);
  },

  async getUserWatchlist(userId: string) {
    const { data, error } = await supabase
      .from('drop_watchlist')
      .select('*, drops(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },
};

export const userInterestService = {
  async getByUser(userId: string) {
    const { data, error } = await supabase
      .from('user_interests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async add(userId: string, category: string, value: string) {
    return supabase.from('user_interests').upsert({ user_id: userId, category, value }, { onConflict: 'user_id,category,value' });
  },

  async remove(userId: string, category: string, value: string) {
    return supabase.from('user_interests').delete().match({ user_id: userId, category, value });
  },
};

// ── Seller Rep Scores ────────────────────────────────────────

export interface SellerRepScore {
  id: string;
  seller_id: string;
  sales_score: number;
  review_score: number;
  response_time_score: number;
  shipping_speed_score: number;
  completion_rate_score: number;
  account_age_score: number;
  verification_bonus: number;
  total_sales: number;
  total_reviews: number;
  review_avg: number;
  avg_response_minutes: number;
  avg_ship_hours: number;
  orders_completed: number;
  orders_disputed: number;
  orders_cancelled: number;
  overall_score: number;
  tier: string;
  created_at: string;
  updated_at: string;
}

export interface SellerReview {
  id: string;
  order_id: string | null;
  seller_id: string;
  reviewer_id: string;
  rating: number;
  title: string;
  content: string;
  communication_rating: number | null;
  shipping_rating: number | null;
  accuracy_rating: number | null;
  created_at: string;
}

function computeTier(score: number): string {
  if (score >= 90) return 'Diamond';
  if (score >= 75) return 'Platinum';
  if (score >= 55) return 'Gold';
  if (score >= 30) return 'Silver';
  return 'Bronze';
}

export const repScoreService = {
  async getBySellerID(sellerId: string): Promise<{ data: SellerRepScore | null; error: any }> {
    const { data, error } = await supabase
      .from('seller_rep_scores')
      .select('*')
      .eq('seller_id', sellerId)
      .maybeSingle();
    return { data: data as SellerRepScore | null, error };
  },

  async recalculate(sellerId: string): Promise<{ data: SellerRepScore | null; error: any }> {
    const [ordersRes, reviewsRes, messagesRes, profileRes] = await Promise.all([
      supabase.from('orders').select('*').or(`seller_id.eq.${sellerId},buyer_id.eq.${sellerId}`),
      supabase.from('seller_reviews').select('*').eq('seller_id', sellerId),
      supabase.from('messages').select('created_at').eq('sender_id', sellerId).order('created_at', { ascending: false }).limit(50),
      supabase.from('profiles').select('created_at, verified').eq('id', sellerId).single(),
    ]);

    const orders = ordersRes.data || [];
    const reviews = reviewsRes.data || [];
    const profile = profileRes.data;

    const sellerOrders = orders.filter((o: any) => o.seller_id === sellerId);
    const completedOrders = sellerOrders.filter((o: any) => o.status === 'completed');
    const disputedOrders = sellerOrders.filter((o: any) => o.status === 'disputed');
    const totalSellerOrders = sellerOrders.length;

    // Sales score: 0-20 (logarithmic scale)
    const totalSales = completedOrders.length;
    const salesScore = Math.min(20, Math.round(Math.log2(totalSales + 1) * 5));

    // Review score: 0-25 (based on avg rating)
    const totalReviews = reviews.length;
    const reviewAvg = totalReviews > 0
      ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / totalReviews
      : 0;
    const reviewScore = Math.min(25, Math.round(reviewAvg * 5));

    // Response time score: 0-15
    let avgResponseMinutes = 0;
    let responseTimeScore = 0;
    if (messagesRes.data && messagesRes.data.length > 1) {
      const gaps: number[] = [];
      for (let i = 1; i < messagesRes.data.length; i++) {
        const diff = new Date(messagesRes.data[i - 1].created_at).getTime() -
                     new Date(messagesRes.data[i].created_at).getTime();
        if (diff > 0 && diff < 86400000) gaps.push(diff / 60000);
      }
      avgResponseMinutes = gaps.length > 0 ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : 0;
    }
    if (avgResponseMinutes <= 60) responseTimeScore = 15;
    else if (avgResponseMinutes <= 240) responseTimeScore = 12;
    else if (avgResponseMinutes <= 720) responseTimeScore = 8;
    else if (avgResponseMinutes <= 1440) responseTimeScore = 5;
    else responseTimeScore = 2;
    if (avgResponseMinutes === 0) responseTimeScore = 5;

    // Shipping speed score: 0-15 (from order created to shipped)
    const shippedOrders = sellerOrders.filter((o: any) => o.tracking_number);
    let avgShipHours = 0;
    let shippingSpeedScore = 5;
    if (shippedOrders.length > 0) {
      const shipTimes = shippedOrders.map((o: any) => {
        const created = new Date(o.created_at).getTime();
        const shipped = new Date(o.updated_at).getTime();
        return Math.max(0, (shipped - created) / 3600000);
      });
      avgShipHours = Math.round(shipTimes.reduce((a: number, b: number) => a + b, 0) / shipTimes.length);
      if (avgShipHours <= 24) shippingSpeedScore = 15;
      else if (avgShipHours <= 48) shippingSpeedScore = 12;
      else if (avgShipHours <= 72) shippingSpeedScore = 8;
      else shippingSpeedScore = 3;
    }

    // Completion rate score: 0-15
    const completionRate = totalSellerOrders > 0 ? completedOrders.length / totalSellerOrders : 0;
    const completionRateScore = Math.min(15, Math.round(completionRate * 15));

    // Account age score: 0-5 (1pt per 3 months)
    let accountAgeScore = 0;
    if (profile?.created_at) {
      const months = (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24 * 30);
      accountAgeScore = Math.min(5, Math.floor(months / 3));
    }

    // Verification bonus: 0-5
    const verificationBonus = profile?.verified ? 5 : 0;

    const overallScore = Math.min(100,
      salesScore + reviewScore + responseTimeScore +
      shippingSpeedScore + completionRateScore + accountAgeScore + verificationBonus
    );

    const tier = computeTier(overallScore);

    const scoreData = {
      seller_id: sellerId,
      sales_score: salesScore,
      review_score: reviewScore,
      response_time_score: responseTimeScore,
      shipping_speed_score: shippingSpeedScore,
      completion_rate_score: completionRateScore,
      account_age_score: accountAgeScore,
      verification_bonus: verificationBonus,
      total_sales: totalSales,
      total_reviews: totalReviews,
      review_avg: Math.round(reviewAvg * 10) / 10,
      avg_response_minutes: avgResponseMinutes,
      avg_ship_hours: avgShipHours,
      orders_completed: completedOrders.length,
      orders_disputed: disputedOrders.length,
      orders_cancelled: sellerOrders.filter((o: any) => o.status === 'cancelled').length,
      overall_score: overallScore,
      tier,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('seller_rep_scores')
      .upsert(scoreData, { onConflict: 'seller_id' })
      .select('*')
      .single();

    return { data: data as SellerRepScore | null, error };
  },

  async getReviews(sellerId: string, limit = 20): Promise<{ data: any[]; error: any }> {
    const { data, error } = await supabase
      .from('seller_reviews')
      .select('*, reviewer:profiles!seller_reviews_reviewer_id_fkey(username, display_name, avatar_url)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async submitReview(review: {
    order_id?: string;
    seller_id: string;
    reviewer_id: string;
    rating: number;
    title?: string;
    content?: string;
    communication_rating?: number;
    shipping_rating?: number;
    accuracy_rating?: number;
  }) {
    const { data, error } = await supabase
      .from('seller_reviews')
      .insert(review)
      .select('*')
      .single();

    if (!error && data) {
      await supabase.from('rep_events').insert({
        seller_id: review.seller_id,
        event_type: 'review_received',
        points_delta: review.rating >= 4 ? 3 : review.rating >= 3 ? 1 : -2,
        metadata: { rating: review.rating, review_id: data.id },
      });
      await repScoreService.recalculate(review.seller_id);
    }

    return { data, error };
  },

  async logEvent(sellerId: string, eventType: string, pointsDelta: number, metadata?: any) {
    await supabase.from('rep_events').insert({
      seller_id: sellerId,
      event_type: eventType,
      points_delta: pointsDelta,
      metadata: metadata || {},
    });
  },

  async getEventHistory(sellerId: string, limit = 50) {
    const { data, error } = await supabase
      .from('rep_events')
      .select('*')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
};

// ── Reports / Admin ───────────────────────────────────────

export const reportService = {
  async getAll(status?: string, limit = 50) {
    let query = supabase
      .from('reports')
      .select('*, reporter:profiles!reporter_id(username, display_name, avatar_url)')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (status && status !== 'all') query = query.eq('status', status.toUpperCase());
    const { data, error } = await query;
    return { data: data || [], error };
  },

  async updateStatus(reportId: string, status: string, resolution?: string) {
    const updates: Record<string, any> = { status: status.toUpperCase() };
    if (resolution) updates.resolution = resolution;
    if (status === 'RESOLVED' || status === 'DISMISSED') updates.resolved_at = new Date().toISOString();
    return supabase.from('reports').update(updates).eq('id', reportId);
  },

  async addNote(reportId: string, adminId: string, content: string) {
    return supabase.from('admin_notes').insert({
      admin_id: adminId,
      target_type: 'report',
      target_id: reportId,
      content,
    });
  },

  async getNotesForReport(reportId: string) {
    const { data, error } = await supabase
      .from('admin_notes')
      .select('*')
      .eq('target_type', 'report')
      .eq('target_id', reportId)
      .order('created_at', { ascending: true });
    return { data: data || [], error };
  },

  async getStats() {
    const [pending, total, resolved] = await Promise.all([
      supabase.from('reports').select('*', { count: 'exact', head: true }).eq('status', 'PENDING'),
      supabase.from('reports').select('*', { count: 'exact', head: true }),
      supabase.from('reports').select('*', { count: 'exact', head: true }).in('status', ['RESOLVED', 'DISMISSED']),
    ]);
    return { pending: pending.count || 0, total: total.count || 0, resolved: resolved.count || 0 };
  },
};

export const auditLogService = {
  async getRecent(limit = 30) {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*, admin:admin_users(display_name)')
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },
};

// ── Platform Stats (Admin) ────────────────────────────────

export const platformStatsService = {
  async getOverview() {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const weekAgo = new Date(now.getTime() - 7 * 86400000).toISOString();

    const [users, listings, orders, reports, activities] = await Promise.all([
      supabase.from('profiles').select('*', { count: 'exact', head: true }),
      supabase.from('listings').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('orders').select('price').gte('created_at', weekAgo),
      supabase.from('reports').select('*', { count: 'exact', head: true }).eq('status', 'PENDING'),
      supabase.from('activities').select('*', { count: 'exact', head: true }).gte('created_at', todayStart),
    ]);

    const salesVolume = (orders.data || []).reduce((sum: number, o: any) => sum + Number(o.price || 0), 0);

    return {
      totalUsers: users.count || 0,
      activeListings: listings.count || 0,
      salesVolume,
      pendingReports: reports.count || 0,
      todayActivity: activities.count || 0,
      weeklyOrders: (orders.data || []).length,
    };
  },
};

// ── Seller Reviews (for satisfaction metrics) ─────────────

export const sellerReviewService = {
  async getForSeller(sellerId: string, limit = 20) {
    const { data, error } = await supabase
      .from('seller_reviews')
      .select('*, reviewer:profiles!reviewer_id(username, display_name, avatar_url)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async getAverages(sellerId: string) {
    const { data, error } = await supabase
      .from('seller_reviews')
      .select('rating, communication_rating, shipping_rating, accuracy_rating')
      .eq('seller_id', sellerId);
    if (!data || data.length === 0) return { avg: 0, communication: 0, shipping: 0, accuracy: 0, count: 0 };
    const count = data.length;
    const avg = (field: string) => data.reduce((s: number, r: any) => s + (Number(r[field]) || 0), 0) / count;
    return { avg: avg('rating'), communication: avg('communication_rating'), shipping: avg('shipping_rating'), accuracy: avg('accuracy_rating'), count };
  },
};

// ── Listing price history (for price confidence) ──────────

export const priceHistoryService = {
  async getSimilarSales(brand: string | null, condition: string, limit = 10) {
    let query = supabase
      .from('orders')
      .select('price, created_at, listing:listings(title, brand, condition, size)')
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(limit);
    const { data, error } = await query;
    return { data: data || [], error };
  },

  async getAvgPrice(brand: string | null, condition: string) {
    const { data } = await supabase
      .from('orders')
      .select('price')
      .eq('status', 'completed');
    if (!data || data.length === 0) return { avg: 0, count: 0 };
    const avg = data.reduce((s: number, o: any) => s + Number(o.price || 0), 0) / data.length;
    return { avg: Math.round(avg), count: data.length };
  },
};

// ── Mutual follows (for profile connections) ──────────────

export const mutualConnectionService = {
  async getMutual(userId: string, limit = 20) {
    const { data: followers } = await supabase
      .from('follows')
      .select('follower_id, follower:profiles!follower_id(id, username, display_name, avatar_url)')
      .eq('following_id', userId)
      .limit(limit);
    return { data: (followers || []).map((f: any) => f.follower).filter(Boolean) };
  },
};

// ── Lab (District Lab) ────────────────────────────────────

export const labService = {
  async getInsights(limit = 20) {
    const { data, error } = await supabase.from('lab_insights').select('*').order('created_at', { ascending: false }).limit(limit);
    return { data: data || [], error };
  },
  async getTrendItems(limit = 50) {
    const { data, error } = await supabase.from('lab_trend_items').select('*').order('momentum', { ascending: false }).limit(limit);
    return { data: data || [], error };
  },
  async getConfig(key: string) {
    const { data, error } = await supabase.from('lab_config').select('data').eq('key', key).single();
    return { data: data?.data ?? [], error };
  },
  async createInsight(item: { title: string; body: string; type?: string; color_class?: string }) {
    const { data, error } = await supabase.from('lab_insights').insert(item).select().single();
    return { data, error };
  },
  async updateInsight(id: string, updates: Record<string, any>) {
    return supabase.from('lab_insights').update(updates).eq('id', id);
  },
  async deleteInsight(id: string) {
    return supabase.from('lab_insights').delete().eq('id', id);
  },
  async createTrendItem(item: Record<string, any>) {
    const { data, error } = await supabase.from('lab_trend_items').insert(item).select().single();
    return { data, error };
  },
  async updateTrendItem(id: string, updates: Record<string, any>) {
    return supabase.from('lab_trend_items').update(updates).eq('id', id);
  },
  async deleteTrendItem(id: string) {
    return supabase.from('lab_trend_items').delete().eq('id', id);
  },
  async updateConfig(key: string, data: unknown) {
    return supabase.from('lab_config').upsert({ key, data, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  },
};

// ── Heart Features (Emotional Hub) ─────────────────────────

export const heartFeaturesService = {
  async getAll() {
    const { data, error } = await supabase.from('heart_features').select('*').order('sort_order', { ascending: true });
    return { data: data || [], error };
  },
  async create(item: { title: string; link_to: string; description?: string; tag?: string; color?: string; icon?: string; sort_order?: number }) {
    const { data, error } = await supabase.from('heart_features').insert(item).select().single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, any>) {
    return supabase.from('heart_features').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('heart_features').delete().eq('id', id);
  },
};

// ── Growth Programs (Grow Hub) ─────────────────────────────

export const growthProgramsService = {
  async getAll() {
    const { data, error } = await supabase.from('growth_programs').select('*').order('sort_order', { ascending: true });
    return { data: data || [], error };
  },
  async create(item: { title: string; link_to: string; description?: string; tag?: string; color?: string; icon?: string; sort_order?: number }) {
    const { data, error } = await supabase.from('growth_programs').insert(item).select().single();
    return { data, error };
  },
  async update(id: string, updates: Record<string, any>) {
    return supabase.from('growth_programs').update(updates).eq('id', id);
  },
  async delete(id: string) {
    return supabase.from('growth_programs').delete().eq('id', id);
  },
};
